import type { InvoiceStatus } from '@/lib/invoice-types'

export interface StatusCopy {
  label: string
  what: string
}

/**
 * Een bron van waarheid voor de uitleg van factuurstatussen. De statuspill, het
 * actiemenu, de modals en de legenda lezen hieruit, zodat de teksten niet uiteen
 * gaan lopen.
 */
export const STATUS_COPY: Record<InvoiceStatus, StatusCopy> = {
  DRAFT: { label: 'Concept', what: 'Nog vrij te bewerken. Er is nog geen factuurnummer en geen factuurdatum toegekend.' },
  PARKED: { label: 'Geparkeerd', what: 'Klaargezet voor later. Doet niet mee in de maandelijkse concept-flow en niet in bulk-acties.' },
  PENDING_APPROVAL: { label: 'Ter approval', what: 'Ingediend en wacht op goedkeuring. De inhoud staat vast tot hij is goedgekeurd of afgewezen.' },
  APPROVED: { label: 'Approved', what: 'Goedgekeurd met nummer, datum en bevroren PDF, maar nog niet naar de klant. Tot verzending kun je hem nog storneren.' },
  SENT: { label: 'Verzonden', what: 'De klant heeft de factuur en moet betalen. Telt mee in openstaand.' },
  ESCALATED: { label: 'Geëscaleerd', what: 'Verzonden en onbetaald, je bent aan het aanmanen. Geen eindstatus: telt nog mee in openstaand en kan gewoon op betaald.' },
  PAID: { label: 'Betaald', what: 'Het geld is binnen. Uitbetalingen aan mede-ondernemers kunnen hierna lopen.' },
  WITHDRAWN: { label: 'Ingetrokken', what: 'Je hebt de factuur teruggenomen nadat de klant hem had. Meestal met creditnota. De uren komen weer vrij om opnieuw te factureren.' },
  WRITTEN_OFF: { label: 'Oninbaar', what: 'De klant hoort te betalen maar doet het niet. Afgeboekt: telt niet meer mee als omzet of openstaand. Betaalt hij alsnog, dan heropen je hem.' },
  VOIDED: { label: 'Gestorneerd', what: 'Ingetrokken vóór verzending. De klant heeft de factuur nooit gehad, dus er was geen creditnota nodig.' },
  REPLACED: { label: 'Vervangen', what: 'Historisch, afkomstig uit de import. Deze status ontstaat niet meer in het systeem zelf.' },
}

/**
 * Te lang openstaan is bewust geen status maar een afgeleide van de verzenddatum;
 * deze regel voorkomt de verwarring met "ingetrokken". De grens zelf staat in de
 * backend (instelling invoices.attention_days) en komt hier nooit als getal langs:
 * de rij toont de werkelijke leeftijd, wat meer zegt dan de drempel.
 */
export const ATTENTION_COPY =
  'Staat langer open dan de ingestelde grens. Geen status: de factuur staat nog gewoon op verzonden of geëscaleerd. '
  + 'Geteld vanaf verzending, niet vanaf de vervaldatum.'

export function statusLabel(status: InvoiceStatus): string {
  return STATUS_COPY[status].label
}

export function isInvoiceStatus(value: unknown): value is InvoiceStatus {
  return typeof value === 'string' && value in STATUS_COPY
}

/**
 * De tooltiptekst bij een statuspill: de definitie, gevolgd door de opgeslagen
 * reden en datum als die er zijn.
 */
export function statusHint(
  status: InvoiceStatus,
  detail?: { escalatedAt?: string | null; escalationReason?: string | null; closedAt?: string | null; closureReason?: string | null },
): string {
  const parts = [STATUS_COPY[status].what]

  const date = status === 'ESCALATED' ? detail?.escalatedAt : detail?.closedAt
  const reason = status === 'ESCALATED' ? detail?.escalationReason : detail?.closureReason

  if (date || reason) {
    const when = date ? formatDutchDate(date) : null
    parts.push([when ? `Sinds ${when}` : null, reason].filter(Boolean).join(': '))
  }

  return parts.join(' ')
}

function formatDutchDate(iso: string): string {
  const [year, month, day] = iso.split('-')
  return day && month && year ? `${day}-${month}-${year}` : iso
}
